import { configuration } from './configuration'
import { helpers } from './helpers'

type Child = {
  type: { name: string }
  return: { type: { displayName: string } }
  child?: Child
  _debugOwner?: {
    _debugInfo?: { fileName: string; lineNumber: number }
    child?: Child
  }
}

type ElementWithFiber = Record<string, unknown & Child>

const getReactFiberElement = (_element: HTMLElement) => {
  const element = _element as unknown as ElementWithFiber

  const reactFiberKey = Object.keys(element).find(key => key.startsWith('__reactFiber$'))

  if (!reactFiberKey || !element[reactFiberKey]) {
    return
  }

  return element[reactFiberKey]
}

const isDevelopment = () =>
  '__REACT_DEVTOOLS_GLOBAL_HOOK__' in window &&
  typeof window.__REACT_DEVTOOLS_GLOBAL_HOOK__ === 'object' &&
  window.__REACT_DEVTOOLS_GLOBAL_HOOK__ !== null &&
  // onScheduleFiberRoot is available only in development mode
  'onScheduleFiberRoot' in window.__REACT_DEVTOOLS_GLOBAL_HOOK__

const clearTablesListener = () => {
  const clearTableSelectors = document.querySelectorAll('[data-fu-tools-clear-table]')

  clearTableSelectors.forEach(clearSelector => {
    clearSelector.addEventListener('click', e => {
      const tableName =
        (e?.currentTarget as Element)?.getAttribute('data-fu-tools-clear-table') ?? ''
      const tableContent = document.querySelector(`[data-fu-tools-table-content="${tableName}"]`)
      if (!tableContent) {
        return
      }
      tableContent.innerHTML = ''
      helpers.clearNotificationBadge(tableName)
    })
  })
}

const menuItemsListener = () => {
  const menuItems = document.querySelectorAll('[data-fu-tools-menu]')
  menuItems.forEach(item => {
    item.addEventListener('click', () =>
      document
        .querySelector('[data-fu-tools-menu-active]')
        ?.setAttribute('data-fu-tools-menu-active', item.getAttribute('data-fu-tools-menu') ?? '')
    )
  })
}

const toggleSizeListener = () => {
  const devToolsContent = document.querySelector('#fu-tools-content')
  const toggleSize = document.querySelector('#fu-tools-size')

  toggleSize?.addEventListener('click', () =>
    changeDataAttributeStates(devToolsContent, 'data-fu-tools-size', ['small', 'default', 'large'])
  )
}

const toggleDevToolsListener = () => {
  const devToolsContent = document.querySelector('#fu-tools-content')
  const toggleDevToolsButton = document.querySelector('#fu-tools')

  toggleDevToolsButton?.addEventListener('click', () =>
    changeDataAttributeStates(devToolsContent, 'data-fu-tools-content', ['open', 'closed'])
  )

  document.addEventListener('keydown', e => {
    if (e.key !== 'Escape') {
      return
    }
    if (devToolsContent?.getAttribute('data-fu-tools-content') !== 'open') {
      return
    }
    devToolsContent.setAttribute('data-fu-tools-content', 'closed')
  })
}

const searchListener = () => {
  const searchInputs = document.querySelectorAll(
    '[data-fu-tools-search-input]'
  ) as NodeListOf<HTMLElement>

  searchInputs?.forEach(input =>
    input?.addEventListener('input', e => {
      const tabId = input?.getAttribute('data-fu-tools-search-input')
      const searchColumnsIndexRaw = input?.getAttribute('data-fu-tools-search-input-columns')
      const table = document.querySelector(`[data-fu-tools-table-content="${tabId}"]`)

      if (!table || !searchColumnsIndexRaw) {
        return
      }

      const searchColumnsIndex = searchColumnsIndexRaw.split(',').map(Number)
      const searchValue = (e.target as HTMLInputElement).value.toLowerCase()

      const rows = table.querySelectorAll('[fu-tools-table-row]')
      rows.forEach(row => {
        const columns = row.querySelectorAll('div')
        const isVisible = searchColumnsIndex.some(index => {
          const column = columns[index]
          if (!column) {
            return false
          }
          const textContent = column.textContent?.toLowerCase() ?? ''
          return textContent.includes(searchValue)
        })

        row.setAttribute('data-fu-tools-row-visible', isVisible ? 'visible' : 'hidden')
      })
    })
  )
}

const toggleElementSelector = () => {
  document.querySelector('#fu-tools-content')?.setAttribute('data-fu-tools-content', 'closed')

  const elementSelector = document.querySelector('#fu-tools-element-selector')

  const states = ['active', 'inactive']
  const currentState = elementSelector?.getAttribute('data-fu-tools-element-selector')
  const nextState = states[((states.indexOf(currentState ?? '') ?? 0) + 1) % states.length] ?? ''
  document
    .querySelector('#fu-tools-element-selector')
    ?.setAttribute('data-fu-tools-element-selector', nextState)
  document.body.setAttribute('data-fu-tools-element-selector', nextState)
}

const toggleElementSelectorListener = () => {
  const elementSelector = document.querySelector('#fu-tools-element-selector')

  elementSelector?.addEventListener('click', e => {
    e.stopPropagation()
    toggleElementSelector()
  })
}

const reactElementSelectorListener = () => {
  const appElement = document.querySelector('#app')

  // There might be inspiration in the future https://github.com/ArnaudBarre/vite-plugin-react-click-to-component
  appElement?.addEventListener('mouseover', e => {
    if (document.body.getAttribute('data-fu-tools-element-selector') !== 'active') {
      return
    }

    const element = e.target as HTMLElement

    const reactFiberElement = getReactFiberElement(element)

    if (!reactFiberElement) {
      return
    }

    const getFirstRecursiveDebugOwnerWithSource = (
      child: typeof reactFiberElement
    ): { fileName: string; lineNumber: number } | null => {
      if (child._debugOwner?._debugInfo) {
        return child._debugOwner?._debugInfo
      }
      return 'child' in child && child.child && typeof child.child === 'object'
        ? getFirstRecursiveDebugOwnerWithSource(child.child)
        : null
    }

    const componentFileName = getFirstRecursiveDebugOwnerWithSource(reactFiberElement)?.fileName

    if (!componentFileName) {
      return
    }

    const pathParts = componentFileName.split('/')

    element.setAttribute(
      'data-fu-tools-element-name',
      [...new Set([pathParts.at(-2), pathParts.at(-1)])].join('/')
    )
  })

  appElement?.addEventListener('click', e => {
    if (document.body.getAttribute('data-fu-tools-element-selector') !== 'active') {
      return
    }

    e.stopPropagation()
    e.preventDefault()

    toggleElementSelector()

    const element = e.target as HTMLElement

    const reactFiberElement = getReactFiberElement(element)
    if (!reactFiberElement) {
      return
    }

    const getFirstRecursiveDebugOwnerWithSource = (
      child: typeof reactFiberElement
    ): { fileName: string; lineNumber: number } | null => {
      if (child._debugOwner?._debugInfo) {
        return child._debugOwner?._debugInfo
      }
      return 'child' in child && child.child && typeof child.child === 'object'
        ? getFirstRecursiveDebugOwnerWithSource(child.child)
        : null
    }

    const debugSource = getFirstRecursiveDebugOwnerWithSource(reactFiberElement)

    // Skip opening editor while holding Ctrl or Cmd key
    if (debugSource && !(<MouseEvent>e).ctrlKey && !(<MouseEvent>e).metaKey) {
      fetch(
        `${window.location.origin}/__open-in-editor?file=${debugSource.fileName}:${debugSource.lineNumber}`
      )
    }
  })
}

const serverCheckerListener = () => {
  let intervalId: ReturnType<typeof setInterval> | null = null

  const serverChecker = document.querySelector('[data-fu-tools-server-status]')
  const serverStatus = document.querySelector('#fu-tools-server-checker')

  if (!serverChecker || !serverStatus) {
    return
  }

  serverStatus.addEventListener('click', () =>
    (document.querySelector('[data-fu-tools-menu=playground]') as HTMLElement)?.click()
  )

  const checkServer = async (
    entries: IntersectionObserverEntry[],
    _observer: IntersectionObserver
  ) => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) {
        if (intervalId) {
          clearInterval(intervalId)
        }
        return
      }

      const checkServerStatus = async () => {
        try {
          const req = await fetch(`${configuration.apiUrl}?query=%7B__typename%7D`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json;charset=utf-8',
            },
          })
          if (req.ok) {
            serverChecker.setAttribute('data-fu-tools-server-status', 'online')
          } else {
            throw new Error('Server not reachable')
          }
        } catch {
          serverChecker.setAttribute('data-fu-tools-server-status', 'offline')
        }
      }

      checkServerStatus()

      intervalId = setInterval(checkServerStatus, 5000)
    })
  }

  const observer = new IntersectionObserver(checkServer)
  observer.observe(serverStatus)
}

const changeDataAttributeStates = (
  element: Element | null,
  dataAttribute: string,
  states: string[]
) => {
  const currentState = element?.getAttribute(dataAttribute)
  const nextState = states[((states.indexOf(currentState ?? '') ?? 0) + 1) % states.length] ?? ''
  element?.setAttribute(dataAttribute, nextState)
}

export {
  getReactFiberElement,
  clearTablesListener,
  menuItemsListener,
  toggleSizeListener,
  toggleDevToolsListener,
  toggleElementSelectorListener,
  reactElementSelectorListener,
  serverCheckerListener,
  searchListener,
  isDevelopment,
}
